import {onBeforeUnmount, onMounted, ref, Ref} from 'vue';

const UNDO_TIME = 6000;

/**
 * Removes the items from the list at once and from the server only after a while, so the removal can be taken back
 */
export function usePendingRemoval<T extends {id: string}>(
    list: Ref<T[]>,
    remove: (item: T) => Promise<void>,
    onRemoved: () => void,
) {
    const message = ref<string | null>(null);
    let pending: {item: T; index: number}[] = [];
    let timer: number | null = null;

    function removeLater(items: T[], text: string): void {
        commit();

        pending = items
            .map(item => ({item, index: list.value.indexOf(item)}))
            .filter(({index}) => index >= 0)
            .sort((a, b) => a.index - b.index);
        list.value = list.value.filter(item => ! items.includes(item));
        message.value = text;
        timer = window.setTimeout(commit, UNDO_TIME);
    }

    function undo(): void {
        stopTimer();

        const restored = [...list.value];
        pending.forEach(({item, index}) => restored.splice(index, 0, item));

        list.value = restored;
        pending = [];
        message.value = null;
    }

    /**
     * Removes the pending items from the server now
     */
    async function commit(): Promise<void> {
        stopTimer();

        if (pending.length === 0) {
            return;
        }

        const items = pending.map(({item}) => item);
        pending = [];
        message.value = null;

        try {
            // one by one, the removals of one estimate must not overwrite each other
            for (const item of items) {
                await remove(item);
            }
        } catch {
            // the refreshed list shows again what could not be removed
        } finally {
            onRemoved();
        }
    }

    function isPending(id: string): boolean {
        return pending.some(({item}) => item.id === id);
    }

    function pendingItems(): T[] {
        return pending.map(({item}) => item);
    }

    function stopTimer(): void {
        if (timer !== null) {
            window.clearTimeout(timer);
            timer = null;
        }
    }

    function onBeforePageUnload(event: BeforeUnloadEvent): void {
        if (pending.length === 0) {
            return;
        }

        commit();
        // the browser asks before leaving, so the removal has time to reach the server
        event.preventDefault();
        event.returnValue = '';
    }

    onMounted(() => window.addEventListener('beforeunload', onBeforePageUnload));
    onBeforeUnmount(() => {
        window.removeEventListener('beforeunload', onBeforePageUnload);
        commit();
    });

    return {message, removeLater, undo, commit, isPending, pendingItems};
}
